import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import {
  ArrowLeftAltOutlined,
  BlockOutlined,
  EditOutlined,
  MoreVertOutlined,
  VisibilityOutlined,
} from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import { type GlTransactionRow, GlTransactionStatus } from "@/graphql/codegen/graphql"

interface TransactionRowActionsProps {
  row: CellContext<GlTransactionRow, unknown>["row"]
  readOnly?: boolean
  onEdit?: (row: GlTransactionRow) => void
  onReverse?: (row: GlTransactionRow) => void
  onCancel?: (row: GlTransactionRow) => void
}

export const TransactionRowActions: FC<TransactionRowActionsProps> = ({
  row,
  readOnly,
  onEdit,
  onReverse,
  onCancel,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const status = row.original.glTransaction?.status
  const canEdit = status === GlTransactionStatus.Posted || status === GlTransactionStatus.Scheduled
  const isViewOnly = status === GlTransactionStatus.Reversed || status === GlTransactionStatus.Cancelled

  const handleMenuOpen = () => setIsMenuOpen(true)
  const handleMenuClose = () => setIsMenuOpen(false)

  const handleEdit = () => {
    handleMenuClose()
    onEdit?.(row.original)
  }

  const handleReverse = () => {
    handleMenuClose()
    onReverse?.(row.original)
  }

  const handleCancel = () => {
    handleMenuClose()
    onCancel?.(row.original)
  }

  return (
    <Box className='flex items-center justify-end' ref={anchorElRef}>
      <IconButton
        size='small'
        onClick={handleMenuOpen}
        data-testid={`transactions.row-actions.menu-button-${row.original.glTransactionRowId}`}
      >
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isMenuOpen}
        onClose={handleMenuClose}
        data-testid={`transactions.row-actions.menu-${row.original.glTransactionRowId}`}
      >
        {(readOnly || canEdit || isViewOnly) && (
          <MenuItem
            onClick={handleEdit}
            data-testid={`transactions.row-actions.edit-button-${row.original.glTransactionRowId}`}
          >
            <ListItemIcon>{isViewOnly || readOnly ? <VisibilityOutlined /> : <EditOutlined />}</ListItemIcon>
            <ListItemText primary={isViewOnly || readOnly ? "View" : "View / Edit"} />
          </MenuItem>
        )}
        {!readOnly && status === GlTransactionStatus.Posted && (
          <MenuItem
            onClick={handleReverse}
            data-testid={`transactions.row-actions.reverse-button-${row.original.glTransactionRowId}`}
          >
            <ListItemIcon>
              <ArrowLeftAltOutlined />
            </ListItemIcon>
            <ListItemText primary='Reverse Transaction' />
          </MenuItem>
        )}
        {!readOnly && status === GlTransactionStatus.Scheduled && (
          <MenuItem
            onClick={handleCancel}
            data-testid={`transactions.row-actions.cancel-button-${row.original.glTransactionRowId}`}
          >
            <ListItemIcon>
              <BlockOutlined />
            </ListItemIcon>
            <ListItemText primary='Cancel' />
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
